import React, { useState, useEffect, useCallback } from 'react';
import {
  Mail, Users, Send, CheckCircle, AlertCircle,
  UserMinus, RefreshCw, Eye, EyeOff
} from 'lucide-react';
import { adminNewsletterService } from '../services/adminNewsletterService';

const NewsletterManagement = () => {
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [subject, setSubject] = useState('');
  const [content, setContent] = useState('');
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null); // { type: 'success'|'error', message }
  const [showPreview, setShowPreview] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const loadStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const data = await adminNewsletterService.getStats();
      setStats(data);
    } catch (err) {
      console.error('Failed to load newsletter stats:', err);
    } finally {
      setStatsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  const handleSend = async () => {
    setShowConfirm(false);
    setSending(true);
    setSendResult(null);
    try {
      const data = await adminNewsletterService.send(subject, content);
      setSendResult({ type: 'success', message: data.message });
      setSubject('');
      setContent('');
      setShowPreview(false);
      loadStats();
    } catch (err) {
      setSendResult({ type: 'error', message: err.message || 'Failed to send newsletter.' });
    } finally {
      setSending(false);
    }
  };

  const canSend = subject.trim() && content.trim() && !sending;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Newsletter</h1>
          <p className="text-gray-500 text-sm mt-1">Compose and send newsletters to all active subscribers</p>
        </div>
        <button
          onClick={loadStats}
          className="flex items-center gap-2 px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-600"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-5 flex items-center gap-4">
          <div className="p-3 bg-blue-100 rounded-lg">
            <Users className="w-6 h-6 text-blue-600" />
          </div>
          <div>
            <div className="text-2xl font-bold text-gray-900">
              {statsLoading ? '—' : stats?.totalSubscribers ?? 0}
            </div>
            <div className="text-sm text-gray-500">Total Subscribers</div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-5 flex items-center gap-4">
          <div className="p-3 bg-green-100 rounded-lg">
            <Mail className="w-6 h-6 text-green-600" />
          </div>
          <div>
            <div className="text-2xl font-bold text-gray-900">
              {statsLoading ? '—' : stats?.activeSubscribers ?? 0}
            </div>
            <div className="text-sm text-gray-500">Active Subscribers</div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-5 flex items-center gap-4">
          <div className="p-3 bg-red-100 rounded-lg">
            <UserMinus className="w-6 h-6 text-red-500" />
          </div>
          <div>
            <div className="text-2xl font-bold text-gray-900">
              {statsLoading ? '—' : stats?.unsubscribed ?? 0}
            </div>
            <div className="text-sm text-gray-500">Unsubscribed</div>
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Compose */}
        <div className="lg:col-span-2 bg-white rounded-xl border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Compose Newsletter</h2>

          {sendResult && (
            <div className={`flex items-start gap-3 p-4 rounded-lg mb-4 text-sm ${
              sendResult.type === 'success'
                ? 'bg-green-50 text-green-800 border border-green-200'
                : 'bg-red-50 text-red-800 border border-red-200'
            }`}>
              {sendResult.type === 'success'
                ? <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                : <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />}
              <span>{sendResult.message}</span>
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Subject *</label>
              <input
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="e.g. mwbTechs — June 2026 Update"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Content (HTML supported) *</label>
              <textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                rows={12}
                placeholder={'<h2>Hello!</h2>\n<p>Here is what\'s new this month...</p>'}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none font-mono text-sm resize-y"
              />
            </div>

            <div className="flex items-center justify-between pt-2">
              <button
                type="button"
                onClick={() => setShowPreview(!showPreview)}
                disabled={!content.trim()}
                className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 disabled:opacity-40"
              >
                {showPreview ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                {showPreview ? 'Hide Preview' : 'Preview'}
              </button>

              <button
                onClick={() => { setSendResult(null); setShowConfirm(true); }}
                disabled={!canSend}
                className="flex items-center gap-2 px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed font-medium text-sm"
              >
                <Send className="w-4 h-4" />
                Send to {statsLoading ? '…' : stats?.activeSubscribers ?? 0} subscribers
              </button>
            </div>
          </div>

          {/* Preview */}
          {showPreview && content.trim() && (
            <div className="mt-6 border-t border-gray-200 pt-4">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">Email Preview</p>
              <div
                className="border border-gray-200 rounded-lg p-6 bg-gray-50 text-sm overflow-auto max-h-96"
                dangerouslySetInnerHTML={{ __html: content }}
              />
            </div>
          )}
        </div>

        {/* Recent Subscribers */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Recent Subscribers</h2>
          {statsLoading ? (
            <div className="space-y-2">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="h-8 bg-gray-100 rounded animate-pulse" />
              ))}
            </div>
          ) : stats?.recentEmails?.length > 0 ? (
            <ul className="space-y-2">
              {stats.recentEmails.map((email) => (
                <li key={email} className="flex items-center gap-2 text-sm text-gray-700">
                  <Mail className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                  <span className="truncate">{email}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-gray-400 text-center py-6">No subscribers yet</p>
          )}
        </div>
      </div>

      {/* Confirm Send Modal */}
      {showConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6">
            <div className="flex items-center justify-center w-14 h-14 bg-blue-100 rounded-full mx-auto mb-4">
              <Send className="w-7 h-7 text-blue-600" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900 text-center mb-1">Send Newsletter?</h2>
            <p className="text-sm text-gray-500 text-center mb-1">
              This will send <strong>"{subject}"</strong> to
            </p>
            <p className="text-2xl font-bold text-blue-600 text-center mb-4">
              {stats?.activeSubscribers ?? 0} subscribers
            </p>
            <p className="text-xs text-gray-400 text-center mb-6">This action cannot be undone.</p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowConfirm(false)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleSend}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
              >
                Send Now
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NewsletterManagement;
